import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableFooter,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    InputAdornment,
    Grid,
    Paper
} from '@mui/material';
import {
    Schedule,
    Visibility,
    Phone,
    Email,
    WhatsApp,
    LinkedIn,
    GitHub,
    Twitter,
    Instagram,
    Facebook,
    Download,
    TouchApp,
    QrCodeScanner,
    DevicesOther,
    LocationOn,
    Search
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { usePage } from '../context/PageContext';
import { useAuth } from '../context/AuthContext';
import { getCards, getAnalyticsHistory, getAllUsers, getUserCards } from '../api/analyticsApi';

const AnalyticsHistoryPage = () => {
    const { setPageTitle } = usePage();
    const { user } = useAuth();
    const [activities, setActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [timeRange, setTimeRange] = useState('30days');
    const [actionFilter, setActionFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    
    // Admin-specific state
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [userCards, setUserCards] = useState([]);
    const isAdmin = user?.role === 'admin';

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getActionColor = (action) => {
        if (action.includes('View')) return 'primary';
        if (action.includes('Click') || action.includes('Contact')) return 'success';
        if (action.includes('Download')) return 'info';
        if (action.includes('Share') || action.includes('QR')) return 'secondary';
        return 'default';
    };

    const getActionIcon = (action) => {
        if (action.includes('View')) return <Visibility sx={{ fontSize: 16 }} />;
        if (action.includes('Phone')) return <Phone sx={{ fontSize: 16 }} />;
        if (action.includes('Email')) return <Email sx={{ fontSize: 16 }} />;
        if (action.includes('WhatsApp')) return <WhatsApp sx={{ fontSize: 16 }} />;
        if (action.includes('LinkedIn')) return <LinkedIn sx={{ fontSize: 16 }} />;
        if (action.includes('GitHub')) return <GitHub sx={{ fontSize: 16 }} />;
        if (action.includes('Twitter')) return <Twitter sx={{ fontSize: 16 }} />;
        if (action.includes('Instagram')) return <Instagram sx={{ fontSize: 16 }} />;
        if (action.includes('Facebook')) return <Facebook sx={{ fontSize: 16 }} />;
        if (action.includes('Download')) return <Download sx={{ fontSize: 16 }} />;
        if (action.includes('Share') || action.includes('QR')) return <QrCodeScanner sx={{ fontSize: 16 }} />;
        return <TouchApp sx={{ fontSize: 16 }} />;
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return {
            date: date.toLocaleDateString(),
            time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            full: date.toLocaleString()
        };
    };

    const fetchUsers = async () => {
        if (!isAdmin) return;
        
        try {
            const usersData = await getAllUsers();
            setUsers(usersData || []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setUsers([]);
        }
    };

    const fetchCards = async () => {
        try {
            const cardsData = await getCards();
            
            if (cardsData && Array.isArray(cardsData)) {
                setCards(cardsData);
                if (cardsData.length > 0 && !selectedCard) {
                    setSelectedCard(cardsData[0]);
                }
            } else {
                setCards([]);
            }
        } catch (error) {
            console.error('Error fetching cards:', error);
            setCards([]);
        }
    };

    const fetchUserCards = async (userId) => {
        if (!isAdmin) return;
        
        try {
            const cardsData = await getUserCards(userId);
            setUserCards(cardsData || []);
            
            // Set first card as selected if available
            if (cardsData && cardsData.length > 0) {
                setSelectedCard(cardsData[0]);
            } else {
                setSelectedCard(null);
            }
        } catch (error) {
            console.error('Failed to fetch user cards:', error);
            setUserCards([]);
            setSelectedCard(null);
        }
    };

    const fetchAnalyticsHistory = async () => {
        if (!selectedCard) {
            setActivities([]);
            setFilteredActivities([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const days = getDaysFromTimeRange(timeRange);
            const response = await getAnalyticsHistory(selectedCard.id, {
                days: days,
                page: 0,
                size: 1000, // Get all data for client-side filtering
                actionFilter: 'all',
                search: ''
            });
            
            if (response && response.activities) {
                setActivities(response.activities);
                setFilteredActivities(response.activities);
            } else {
                setActivities([]);
                setFilteredActivities([]);
            }
        } catch (error) {
            console.error('Error fetching analytics history:', error);
            setActivities([]);
            setFilteredActivities([]);
        }
        setLoading(false);
    };

    const getDaysFromTimeRange = (range) => {
        switch (range) {
            case '7days': return 7;
            case '30days': return 30;
            case '90days': return 90;
            case 'all': return 365; // Get last year for "all time"
            default: return 30;
        }
    };

    const handleUserChange = (e) => {
        const newUser = e.target.value;
        setSelectedUser(newUser);
        setSelectedCard(null); // Reset card selection when user changes
        
        if (newUser !== 'all') {
            fetchUserCards(newUser);
        } else {
            setUserCards([]);
            // For "all users", use the main cards list
            if (cards.length > 0) {
                setSelectedCard(cards[0]);
            }
        }
    };

    const getCardDropdownLabel = () => {
        if (!isAdmin) return 'Select Card';
        
        if (selectedUser === 'all') {
            return 'All Cards (All Users)';
        } else {
            const userData = users.find(u => u.id === parseInt(selectedUser));
            return userData ? `All Cards (${userData.username})` : 'All Cards (Selected User)';
        }
    };

    const getCardDropdownOptions = () => {
        if (!isAdmin) {
            return cards;
        }
        
        if (selectedUser === 'all') {
            return cards; // All cards from all users
        } else {
            return userCards; // Cards from selected user
        }
    };

    // Filter activities based on search term and action filter
    useEffect(() => {
        if (!activities || activities.length === 0) {
            setFilteredActivities([]);
            return;
        }

        let filtered = activities;

        if (actionFilter !== 'all') {
            filtered = filtered.filter(activity => 
                activity.action && activity.action.toLowerCase().includes(actionFilter.toLowerCase())
            );
        }

        if (searchTerm) {
            filtered = filtered.filter(activity => 
                (activity.action && activity.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (activity.location && activity.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (activity.device && activity.device.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (activity.ip_address && activity.ip_address.includes(searchTerm))
            );
        }

        setFilteredActivities(filtered);
        setPage(0); // Reset to first page when filtering
    }, [activities, actionFilter, searchTerm]);

    useEffect(() => {
        setPageTitle('Analytics History');
        fetchCards();
        if (isAdmin) {
            fetchUsers();
        }
    }, [setPageTitle, isAdmin]);

    useEffect(() => {
        fetchAnalyticsHistory();
    }, [selectedCard, timeRange]);

    // Calculate pagination
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedActivities = (filteredActivities && filteredActivities.length > 0) ? 
        filteredActivities.slice(startIndex, endIndex) : [];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Typography>Loading analytics history...</Typography>
            </Box>
        );
    }

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <Box>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 'bold', 
                        mb: 1,
                        fontSize: { xs: '1.2rem', sm: '1.8rem' }
                    }}>
                        Analytics History
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Detailed view of all card interactions and visitor activities
                    </Typography>
                </Box>

                {/* Filters */}
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4, borderRadius: 3 }}>
                    <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                        {/* User Selection (Admin only) */}
                        {isAdmin && (
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>User Selection</InputLabel>
                                    <Select
                                        value={selectedUser}
                                        onChange={handleUserChange}
                                        label="User Selection"
                                    >
                                        <MenuItem value="all">
                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                All Users
                                            </Typography>
                                        </MenuItem>
                                        {users.map((userData) => (
                                            <MenuItem key={userData.id} value={userData.id}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography>{userData.username}</Typography>
                                                        {userData.role === 'admin' && (
                                                            <Chip label="Admin" size="small" color="primary" />
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {userData.card_count} cards
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        
                        {/* Card Selection */}
                        <Grid item xs={12} md={isAdmin ? 3 : 4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Card Selection</InputLabel>
                                <Select
                                    value={selectedCard?.id || ''}
                                    onChange={(e) => {
                                        const cardOptions = getCardDropdownOptions();
                                        if (cardOptions && cardOptions.length > 0) {
                                            const card = cardOptions.find(p => p.id === e.target.value);
                                            setSelectedCard(card);
                                        }
                                    }}
                                    label="Card Selection"
                                >
                                    {getCardDropdownOptions() && getCardDropdownOptions().length > 0 ? getCardDropdownOptions().map((card) => (
                                        <MenuItem key={card.id} value={card.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography>{card.name}</Typography>
                                                    {!card.is_published && (
                                                        <Chip label="Draft" size="small" color="warning" />
                                                    )}
                                                </Box>
                                                {isAdmin && card.owner && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        by {card.owner.username}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    )) : (
                                        <MenuItem value="" disabled>
                                            No cards available
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Time Range</InputLabel>
                                <Select
                                    value={timeRange}
                                    onChange={(e) => setTimeRange(e.target.value)}
                                    label="Time Range"
                                >
                                    <MenuItem value="7days">Last 7 Days</MenuItem>
                                    <MenuItem value="30days">Last 30 Days</MenuItem>
                                    <MenuItem value="90days">Last 3 Months</MenuItem>
                                    <MenuItem value="all">All Time</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Action Type</InputLabel>
                                <Select
                                    value={actionFilter}
                                    onChange={(e) => setActionFilter(e.target.value)}
                                    label="Action Type"
                                >
                                    <MenuItem value="all">All Actions</MenuItem>
                                    <MenuItem value="view">Views</MenuItem>
                                    <MenuItem value="click">Clicks</MenuItem>
                                    <MenuItem value="download">Downloads</MenuItem>
                                    <MenuItem value="share">Shares</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Analytics History Table */}
                <Card elevation={3} sx={{ borderRadius: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Schedule sx={{ mr: 2, color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Activity Log
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Chip 
                                    label={`${filteredActivities?.length || 0} activities`} 
                                    size="small" 
                                    color="primary"
                                /> 
                                <Chip 
                                    label={`${activities?.length || 0} total`} 
                                    size="small" 
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                        
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date & Time</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Device</TableCell>
                                        <TableCell>IP Address</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedActivities.map((activity, index) => {
                                        const timestamp = formatTimestamp(activity.timestamp);
                                        return (
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {timestamp.date}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {timestamp.time}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {getActionIcon(activity.action)}
                                                        <Chip 
                                                            label={activity.action} 
                                                            size="small" 
                                                            color={getActionColor(activity.action)}
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2">
                                                            {activity.location || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <DevicesOther sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2">
                                                            {activity.device || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {activity.ip_address}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {filteredActivities.length > 0 && (
                            <TablePagination
                                component="div"
                                count={filteredActivities.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                showFirstButton
                                showLastButton
                            />
                        )}
                        
                        {filteredActivities.length === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                <Typography color="text.secondary">
                                    No activities found matching your criteria
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </motion.div>
    );
};

export default AnalyticsHistoryPage; 